/**
 * Playbook thresholds: the built-in review framework's benchmarks, which
 * finance can tune. Code values remain the defaults and can always be restored.
 */

import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Pencil, RotateCcw, Search } from 'lucide-react';
import { usePermissions } from '../../../hooks/usePermissions';
import {
  usePlaybooks,
  usePlaybookThresholds,
  useResetPlaybookOverride,
  useSavePlaybookOverride
} from '../../../hooks/queries/useRules';
import { PlaybookParameterRow } from '../../../services/api/ruleService';
import { ParameterEditModal } from './ParameterEditModal';
import { CLASSIFICATION_LABELS, personName, unitLabel } from './ruleBookConstants';

/** The parameter's effective thresholds in one line. */
const summarise = (p: PlaybookParameterRow): string => {
  if (p.override?.enabled === false) return 'Not checked';
  const a = { ...p.defaults.args, ...(p.override?.args || {}) };
  const u = unitLabel(p.extraction.unit);
  const n = (v: number) => (u === '%' ? `${v}%` : u ? `${v} ${u}` : String(v));
  const c = (v: keyof typeof CLASSIFICATION_LABELS) => CLASSIFICATION_LABELS[v] ?? v;

  switch (p.evaluator.kind) {
    case 'atLeast':
    case 'atLeastUnlessLowInvestment':
      return `Preferred ${n(a.preferred)} or more · fallback ${n(a.fallback)}` +
        (a.criticalBelow != null ? ` · critical under ${n(a.criticalBelow)}` : '');
    case 'atMost':
      return `Preferred ${n(a.preferred)} or less · fallback ${n(a.fallback)}`;
    case 'withinRange':
      return `Preferred ${n(a.min)}–${n(a.max)} · tolerable ${n(a.toleranceMin)}–${n(a.toleranceMax)}`;
    case 'mustBePresent':
      return `Must be present · missing is ${c(a.severityWhenMissing)}`;
    case 'mustBeAbsent':
    case 'mustBeAbsentAndUnconditional':
      return `Must be absent · present is ${c(a.severityWhenPresent)}`;
    case 'byOption':
      return Object.entries(a.mapping || {}).map(([o, cls]) => `${o}: ${c(cls as any)}`).join(' · ');
    case 'atLeastPeriods':
      return `${a.preferred} or more consecutive periods`;
    case 'percentThreshold':
      return `Preferred ${a.preferredMax}% or less`;
    default:
      return 'Used with other clauses only';
  }
};

export const PlaybookThresholdsTab: React.FC = () => {
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission('rules.edit');

  const { data: playbooks = [] } = usePlaybooks();
  const [playbookId, setPlaybookId] = useState<string>();
  const activeId = playbookId ?? playbooks[0]?.id;
  const { data, isLoading, error } = usePlaybookThresholds(activeId);

  const save = useSavePlaybookOverride();
  const reset = useResetPlaybookOverride();

  const [search, setSearch] = useState('');
  const [modifiedOnly, setModifiedOnly] = useState(false);
  const [open, setOpen] = useState<Set<number>>(new Set());
  const [editing, setEditing] = useState<PlaybookParameterRow | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const groups = useMemo(() => {
    if (!data) return [];
    const byId = new Map(data.parameters.map(p => [p.id, p]));
    const q = search.trim().toLowerCase();
    return data.topics
      .map(topic => ({
        topic,
        params: topic.parameterIds
          .map(id => byId.get(id))
          .filter((p): p is PlaybookParameterRow => !!p)
          .filter(p => !modifiedOnly || !!p.override)
          .filter(p => !q || p.label.toLowerCase().includes(q) || summarise(p).toLowerCase().includes(q))
      }))
      .filter(g => g.params.length > 0);
  }, [data, search, modifiedOnly]);

  const modifiedCount = data?.parameters.filter(p => p.override).length ?? 0;
  // Searching or filtering opens every matching group, so results are never hidden
  const isOpen = (n: number) => !!search.trim() || modifiedOnly || open.has(n);
  const toggle = (n: number) => setOpen(prev => {
    const next = new Set(prev);
    next.has(n) ? next.delete(n) : next.add(n);
    return next;
  });

  if (!activeId && !isLoading) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">No review playbooks are registered.</p>;
  }

  return (
    <div className="space-y-5">
      {data && (
        <div className="rounded-xl border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface p-4">
          <div className="flex flex-wrap items-center gap-3">
            {playbooks.length > 1 ? (
              <select
                aria-label="Playbook"
                className="rounded-lg border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg-secondary pl-3 pr-9 py-2 text-sm"
                value={activeId}
                onChange={e => setPlaybookId(e.target.value)}
              >
                {playbooks.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            ) : (
              <h3 className="text-base font-medium text-gray-900 dark:text-white">{data.name}</h3>
            )}
            <span className="text-xs text-gray-500 dark:text-gray-400">
              v{data.version} &middot; {data.perspective} &middot; applies to {data.appliesTo.join(', ')} contracts
            </span>
          </div>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {data.parameters.length} benchmarks, {modifiedCount} changed from the default. The AI extracts what each
            contract says; these thresholds decide how it is rated.
          </p>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search benchmarks"
            aria-label="Search benchmarks"
            className="w-full rounded-lg border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg-secondary pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400"
          />
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input type="checkbox" checked={modifiedOnly} onChange={e => setModifiedOnly(e.target.checked)} className="h-4 w-4 rounded border-gray-300" />
          Changed only ({modifiedCount})
        </label>
      </div>

      {actionError && (
        <p role="alert" className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {actionError}
        </p>
      )}

      {isLoading ? (
        <div className="space-y-3">
          {[0, 1, 2, 3].map(i => <div key={i} className="h-14 rounded-xl bg-gray-100 dark:bg-dark-bg-secondary animate-pulse" />)}
        </div>
      ) : error ? (
        <p className="text-sm text-red-600">Couldn't load the playbook: {(error as Error).message}</p>
      ) : groups.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">No benchmarks match.</p>
      ) : (
        <div className="space-y-3">
          {groups.map(({ topic, params }) => {
            const expanded = isOpen(topic.number);
            const changed = params.filter(p => p.override).length;
            return (
              <section key={topic.number} className="rounded-xl border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface overflow-hidden">
                <button
                  onClick={() => toggle(topic.number)}
                  aria-expanded={expanded}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-dark-bg-secondary"
                >
                  {expanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                  <span className="flex-1 font-medium text-gray-900 dark:text-white">
                    {topic.number}. {topic.title}
                  </span>
                  {changed > 0 && (
                    <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 text-xs font-medium">
                      {changed} changed
                    </span>
                  )}
                  <span className="text-xs text-gray-500 dark:text-gray-400">{params.length}</span>
                </button>

                {expanded && (
                  <ul className="divide-y divide-gray-100 dark:divide-dark-border border-t border-gray-100 dark:border-dark-border">
                    {params.map(p => {
                      const off = p.override?.enabled === false;
                      return (
                        <li key={p.id} className={`flex items-start gap-3 px-4 py-3 ${off ? 'opacity-60' : ''}`}>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-medium text-gray-900 dark:text-white">{p.label}</span>
                              {p.override && !off && (
                                <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 text-[11px] font-medium">Changed</span>
                              )}
                              {off && (
                                <span className="px-1.5 py-0.5 rounded bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200 text-[11px] font-medium">Off</span>
                              )}
                            </div>
                            <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-400">{summarise(p)}</p>
                            {p.override && (
                              <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">
                                Changed by {personName(p.override.updatedBy)} {new Date(p.override.updatedAt).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                          {canEdit && (
                            <div className="flex items-center gap-1 flex-shrink-0">
                              {p.override && (
                                <button
                                  onClick={() => {
                                    setActionError(null);
                                    reset.mutateAsync({ playbookId: activeId!, parameterId: p.id })
                                      .catch((e: Error) => setActionError(e.message));
                                  }}
                                  disabled={reset.isPending}
                                  title="Reset to default"
                                  aria-label={`Reset ${p.label} to default`}
                                  className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-dark-bg-secondary"
                                >
                                  <RotateCcw className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                onClick={() => { save.reset(); setEditing(p); }}
                                title="Edit"
                                aria-label={`Edit ${p.label}`}
                                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-dark-bg-secondary"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            );
          })}
        </div>
      )}

      {editing && activeId && (
        <ParameterEditModal
          parameter={editing}
          saving={save.isPending}
          error={save.error?.message ?? null}
          onSave={fields => save.mutate(
            { playbookId: activeId, parameterId: editing.id, fields },
            { onSuccess: () => setEditing(null) }
          )}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
};

export default PlaybookThresholdsTab;
